/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  ExchangeInlineLearnMore,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from '../ProvisionModal/share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { ProvisionApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { ServiceForReAuth } from '@Types/administrator';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';
import { MultiAppsAuthenticationInfoRes } from '@Types/provision';
import { App, Service } from '@Types/app';

enum ExchangeInlineStatus {
  NotProvision = 0,
  ProvisioningGrantStep1Done = 1,
  ProvisioningConfirmDone = 2,
  ProvisioningGrantStep2Done = 3,
}

const ExchangeInlineReAuthModal = ({
  orgId,
  onClose,
  authAccount,
}: {
  orgId: string;
  authAccount: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('provision');
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [exchangeInlineStatus, setExchangeInlineStatus] =
    useState<ExchangeInlineStatus>(ExchangeInlineStatus.NotProvision);

  //example:
  // [
  //   {
  //     app: 'exchangeInlineMailFlow',
  //     installAppUrl: null,
  //     authenticationUrl:
  //       'https://login.microsoftonline.com/common/oauth2/authorize?client_id=8af0038e-67da-438d-9424-ce0e1e5f6325&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_flow_inline_ex_72251c12c07a15cdcea1993e218c9620&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html',
  //     appId: '8af0038e-67da-438d-9424-ce0e1e5f6325',
  //   },
  //   {
  //     app: 'exchangeInline',
  //     installAppUrl: null,
  //     authenticationUrl:
  //       'https://login.microsoftonline.com/common/oauth2/authorize?client_id=f362d789-5a05-4faa-9708-70933c56e3ab&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_inline_graph_api_72251c12c07a15cdcea1993e218c9620&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html',
  //     appId: 'f362d789-5a05-4faa-9708-70933c56e3ab',
  //   },
  // ];
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | MultiAppsAuthenticationInfoRes>(null);

  useEffect(() => {
    if (
      exchangeInlineStatus === ExchangeInlineStatus.NotProvision ||
      exchangeInlineStatus === ExchangeInlineStatus.ProvisioningGrantStep1Done
    ) {
      ProvisionApi.apiGetMultiAppsReAuthAuthenticationInfos(
        Service.ExchangeInline,
        orgId
      ).then((res) => {
        setAuthenticationInfo(res.data);
      });
    }
  }, []);

  if (!authenticationInfo) {
    return <></>;
  }
  return (
    <>
      <Modal isOpen={true} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(`provision.reAuth.service.exchangeInline.title`)}
          </ModalHeader>

          <ExchangeInlineLearnMore />

          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <GrantPermissionSteps
              {...{
                exchangeInlineStatus,
                setExchangeInlineStatus,
                authenticationInfo,
                orgId,
                setErrorMsg,
                authAccount,
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExchangeInlineReAuthModal;

//eslint-disable-next-line @typescript-eslint/no-unused-vars
const GrantPermissionSteps = ({
  exchangeInlineStatus,
  setExchangeInlineStatus,
  authenticationInfo,
  orgId,
  setErrorMsg,
  authAccount,
}: {
  exchangeInlineStatus: ExchangeInlineStatus;
  setExchangeInlineStatus: (status: ExchangeInlineStatus) => void;
  authenticationInfo: MultiAppsAuthenticationInfoRes;
  orgId: string;
  setErrorMsg: (msg: string | undefined) => void;
  authAccount: string;
}) => {
  const { t } = useTranslation(['provision', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);

  const onFirstPermission = () => {
    const authUrl = authenticationInfo[0].authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service: 'exchangeInlineMailFlow',
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const exchangeInlineProvisionInfo = getProvisionCookie(
        'provision_status_azuread_mail_flow_inline_ex'
      );
      if (exchangeInlineProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        //{"code": "123"}
        const { error, code } = JSON.parse(exchangeInlineProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfo(
              App.ExchangeInlineMailFlow,
              code,
              authAccount,
              orgId
            );
            if (res.data?.status === 'failure') {
              // failure data = {
              //     "status": "failure",
              //     "reason": "FAILURE;451"  |  "PROVISION;CONFLICT"
              // }
              setErrorMsg(
                t([
                  `provision.service.exchangeInline.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setExchangeInlineStatus(
                ExchangeInlineStatus.ProvisioningGrantStep1Done
              );
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.exchangeInline.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  const onSecondPermission = () => {
    const authUrl = authenticationInfo[1].authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service: 'exchangeInline',
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const exchangeInlineProvisionInfo = getProvisionCookie(
        'provision_status_azuread_mail_inline_graph_api'
      );
      if (exchangeInlineProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(exchangeInlineProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfo(
              ServiceForReAuth.ExchangeInline,
              code,
              authAccount,
              orgId
            );
            if (res.data?.message === 'failure') {
              // failure data = { status: -1, message: 'failure', reason: 'FAILURE;451' };
              setErrorMsg(
                t([
                  `provision.service.exchangeInline.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setExchangeInlineStatus(
                ExchangeInlineStatus.ProvisioningGrantStep2Done
              );
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.exchangeInline.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Steps
        direction="vertical"
        current={exchangeInlineStatus - 1}
        items={[
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t(
                  'provision.service.exchangeInline.configureMailFlowPermission'
                )}
              </Text>
            ),
            description:
              exchangeInlineStatus <
              ExchangeInlineStatus.ProvisioningGrantStep1Done ? (
                <Button
                  variant="secondary"
                  borderRadius="2rem"
                  onClick={() => {
                    onFirstPermission();
                  }}
                >
                  {t('provision.button.grantPermission')}
                </Button>
              ) : (
                <Text color={colorStyle.color.tertiary}>
                  {t('provision.common.permissionGranted')}
                </Text>
              ),
            icon: (
              <StepIcon
                checked={
                  exchangeInlineStatus >=
                  ExchangeInlineStatus.ProvisioningGrantStep1Done
                }
              />
            ),
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                <Trans
                  i18nKey="provision.service.exchangeInline.assignAzureADRoles"
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.exchangeInline.instruction,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            ),
            description: (
              <>
                {exchangeInlineStatus <
                ExchangeInlineStatus.ProvisioningConfirmDone ? (
                  <Button
                    variant="secondary"
                    borderRadius="2rem"
                    onClick={() => {
                      setErrorMsg(undefined);
                      //exchange_inline/check_exchange_admin_role
                      ProvisionApi.apiCheckExchangeAdminRole(orgId)
                        .then((res) => {
                          if (res.data) {
                            setExchangeInlineStatus(
                              ExchangeInlineStatus.ProvisioningConfirmDone
                            );
                          } else {
                            setErrorMsg(
                              t(
                                'provision.service.exchangeInline.error.saveInfo.FAILURE;817'
                              )
                            );
                          }
                        })
                        .catch(() => {
                          setErrorMsg(
                            t(
                              'provision.service.exchangeInline.error.saveInfo.FAILURE;810'
                            )
                          );
                        });
                    }}
                    disabled={
                      !(
                        exchangeInlineStatus ===
                        ExchangeInlineStatus.ProvisioningGrantStep1Done
                      )
                    }
                  >
                    {t('provision.button.confirmRolesAssigned')}
                  </Button>
                ) : (
                  <Text color={colorStyle.color.tertiary}>
                    {t('provision.service.exchangeInline.assignedDone')}
                  </Text>
                )}
                <Alert mt="8px" variant="outline" severity="info">
                  {t('provision.common.assignAppIdTip', {
                    appId: authenticationInfo[0].appId,
                  })}
                </Alert>
              </>
            ),
            icon: <UserIcon />,
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t('provision.service.exchangeInline.syncDataPermission')}
              </Text>
            ),
            description:
              exchangeInlineStatus <
              ExchangeInlineStatus.ProvisioningGrantStep2Done ? (
                <Button
                  variant="secondary"
                  borderRadius="2rem"
                  disabled={
                    !(
                      exchangeInlineStatus ===
                      ExchangeInlineStatus.ProvisioningConfirmDone
                    )
                  }
                  onClick={() => {
                    onSecondPermission();
                  }}
                >
                  {t('provision.button.grantPermission')}
                </Button>
              ) : (
                <Text color={colorStyle.color.tertiary}>
                  {t('provision.common.permissionGranted')}
                </Text>
              ),
            icon: (
              <StepIcon
                checked={
                  exchangeInlineStatus >=
                  ExchangeInlineStatus.ProvisioningGrantStep2Done
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};
